// Generic client-side export helpers for tabular `{columns, rows}` data,
// shared by the assistant structured-report pill and the activity feed
// export. Rendering to CSV/TSV/Excel/PDF happens in the browser from string
// rows the server already formatted, so no server-side document generation
// (and no extra Python dependency) is needed. The spreadsheet
// formula-injection guard lives here so every export path inherits it.

// UTF-8 BOM prepended to the CSV body so Excel and Numbers detect encoding
// when the export contains non-ASCII. Use the escape so prettier or an editor
// "strip BOM" pass can't silently delete it.
export const CSV_BOM = '\uFEFF';
// Keep the blob URL alive long enough for the browser to fetch it before
// revoking. Removing too quickly makes Chromium fall back to opening the blob
// in a new tab instead of saving.
const CSV_REVOKE_DELAY_MS = 4000;
// Reports wider than this render the PDF in landscape so columns fit.
const PDF_LANDSCAPE_COLUMN_THRESHOLD = 4;
const XLSX_MIN_COLUMN_WIDTH = 12;
const XLSX_MAX_COLUMN_WIDTH = 45;
const XLSX_COLUMN_PADDING = 2;

type PdfExportOptions =
    | {
          readonly columnWidths?: undefined;
          readonly repeatColumnIndexes?: never;
      }
    | {
          readonly columnWidths: ReadonlyArray<number>;
          readonly repeatColumnIndexes?: ReadonlyArray<number>;
      };

type XlsxExportOptions = {
    readonly layout?: 'wide-table';
};

// Spreadsheet formula-injection mitigation. Excel, Numbers, and Sheets
// execute a cell starting with `=`, `+`, `-`, `@`, tab, or CR as a formula —
// an attacker-controlled field could exfiltrate other cells via
// `=WEBSERVICE(...)` or similar. Prefixing with a single quote (which
// spreadsheets treat as a literal-text marker and hide) neutralizes the
// formula without changing the visible value once imported.
const FORMULA_INJECTION_LEADING_CHARS = /^[=+\-@\t\r]/;

function neutralizeSpreadsheetFormula(value: string): string {
    return FORMULA_INJECTION_LEADING_CHARS.test(value) ? `'${value}` : value;
}

function escapeCsvCell(value: string): string {
    const safe = neutralizeSpreadsheetFormula(value);
    return `"${safe.replace(/"/g, '""')}"`;
}

// TSV pasted into Excel splits on tab/newline — embedded ones in a cell would
// silently break column alignment. Replace them with spaces so each cell
// stays one column wide. Also apply the same formula-injection neutralizer
// (TSV pastes into spreadsheets too).
function tsvCell(value: string): string {
    return neutralizeSpreadsheetFormula(value.replace(/[\t\r\n]/g, ' '));
}

export function buildTsv(
    columns: ReadonlyArray<string>,
    rows: ReadonlyArray<ReadonlyArray<string>>,
): string {
    return [
        columns.map(tsvCell).join('\t'),
        ...rows.map((row) => row.map(tsvCell).join('\t')),
    ].join('\n');
}

export function buildCsv(
    columns: ReadonlyArray<string>,
    rows: ReadonlyArray<ReadonlyArray<string>>,
): string {
    return [
        columns.map(escapeCsvCell).join(','),
        ...rows.map((row) => row.map(escapeCsvCell).join(',')),
    ].join('\n');
}

// Date-stamped download name, parameterized by extension so every format
// (csv / xlsx / pdf) lands as `<slug>_<YYYY-MM-DD>.<ext>`.
export function reportFilename(slug: string, extension: string): string {
    return `${slug}_${new Date().toISOString().slice(0, 10)}.${extension}`;
}

// Shared blob → download trigger. Extracted so CSV, Excel, and PDF all reuse
// the same Arc/Chromium-safe dispatch + delayed revoke instead of duplicating
// the anchor dance per format.
export function triggerBlobDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    // Dispatch a fully-constructed MouseEvent — Arc/Chromium variants
    // sometimes ignore `download` on .click() and navigate to the blob.
    anchor.dispatchEvent(
        new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
        }),
    );
    window.setTimeout(() => {
        anchor.remove();
        URL.revokeObjectURL(url);
    }, CSV_REVOKE_DELAY_MS);
}

// One Excel cell. Always written as a String so a value beginning with
// `=`/`+`/`-`/`@` is stored as literal text, never a live formula — the .xlsx
// counterpart of the CSV/TSV formula-injection guard (a typed String cell is
// not evaluated as a formula by Excel/Numbers/Sheets).
type XlsxCell = {
    readonly value: string;
    readonly type: StringConstructor;
    readonly fontWeight?: 'bold';
    readonly alignVertical?: 'top';
    readonly wrap?: true;
};

function renderedLineWidth(value: string): number {
    return Math.max(
        ...value.split(/\r\n?|\n/u).map((line) => Array.from(line).length),
    );
}

function buildXlsxColumnWidths(
    columns: ReadonlyArray<string>,
    rows: ReadonlyArray<ReadonlyArray<string>>,
): Array<{ readonly width: number }> {
    return columns.map((column, columnIndex) => {
        const contentWidth = rows.reduce(
            (maxWidth, row) =>
                Math.max(maxWidth, renderedLineWidth(row[columnIndex] ?? '')),
            renderedLineWidth(column),
        );
        return {
            width: Math.min(
                XLSX_MAX_COLUMN_WIDTH,
                Math.max(
                    XLSX_MIN_COLUMN_WIDTH,
                    contentWidth + XLSX_COLUMN_PADDING,
                ),
            ),
        };
    });
}

export function buildXlsxSheetData(
    columns: ReadonlyArray<string>,
    rows: ReadonlyArray<ReadonlyArray<string>>,
    options: XlsxExportOptions = {},
): Array<Array<XlsxCell>> {
    const cellLayout =
        options.layout === 'wide-table'
            ? ({ alignVertical: 'top', wrap: true } as const)
            : {};
    const header: Array<XlsxCell> = columns.map((column) => ({
        value: column,
        type: String,
        fontWeight: 'bold',
        ...cellLayout,
    }));
    const body: Array<Array<XlsxCell>> = rows.map((row) =>
        row.map((cell) => ({
            value: cell,
            type: String,
            ...cellLayout,
        })),
    );
    return [header, ...body];
}

export async function buildXlsxBlob(
    columns: ReadonlyArray<string>,
    rows: ReadonlyArray<ReadonlyArray<string>>,
    options: XlsxExportOptions = {},
): Promise<Blob> {
    // Lazy-load: the xlsx writer only loads on export, staying out of the main
    // bundle. `/universal` runs without a Web Worker; call `.toBlob()` on its
    // return value to get the Blob.
    const writeXlsxFile = (await import('write-excel-file/universal')).default;
    const sheetData = buildXlsxSheetData(columns, rows, options);
    if (options.layout !== 'wide-table') {
        return writeXlsxFile(sheetData).toBlob();
    }
    return writeXlsxFile(sheetData, {
        columns: buildXlsxColumnWidths(columns, rows),
        stickyRowsCount: 1,
        stickyColumnsCount: 1,
    }).toBlob();
}

export async function buildPdfBlob(
    columns: ReadonlyArray<string>,
    rows: ReadonlyArray<ReadonlyArray<string>>,
    title: string,
    options: PdfExportOptions = {},
): Promise<Blob> {
    if (
        options.columnWidths &&
        options.columnWidths.length !== columns.length
    ) {
        throw new Error('PDF column widths must match the exported columns');
    }
    // Lazy-load jsPDF + the table plugin only on export, in parallel.
    const [{ jsPDF }, { autoTable }] = await Promise.all([
        import('jspdf'),
        import('jspdf-autotable'),
    ]);
    // Wide tables read better in landscape.
    const doc = new jsPDF({
        orientation:
            columns.length > PDF_LANDSCAPE_COLUMN_THRESHOLD
                ? 'landscape'
                : 'portrait',
    });
    doc.setFontSize(12);
    const marginX = 14;
    // jsPDF text does not wrap; split a long report title so it stays on the
    // page, and push the table below however many lines it became.
    const titleLines = doc.splitTextToSize(
        title,
        doc.internal.pageSize.getWidth() - marginX * 2,
    );
    const tableStartY = 16 + titleLines.length * 6;
    if (options.columnWidths === undefined) {
        doc.text(titleLines, marginX, 16);
        autoTable(doc, {
            head: [[...columns]],
            body: rows.map((row) => [...row]),
            startY: tableStartY,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [33, 33, 33] },
        });
        return doc.output('blob');
    }
    const columnStyles = Object.fromEntries(
        options.columnWidths.map((cellWidth, index) => [index, { cellWidth }]),
    );
    autoTable(doc, {
        head: [[...columns]],
        body: rows.map((row) => [...row]),
        startY: tableStartY,
        margin: {
            top: tableStartY,
            left: marginX,
            right: marginX,
            bottom: 14,
        },
        styles: {
            cellPadding: 1.5,
            fontSize: 8,
            overflow: 'linebreak',
            valign: 'top',
        },
        headStyles: { fillColor: [33, 33, 33] },
        columnStyles,
        horizontalPageBreak: true,
        horizontalPageBreakRepeat: options.repeatColumnIndexes
            ? [...options.repeatColumnIndexes]
            : undefined,
        horizontalPageBreakBehaviour: 'afterAllRows',
        rowPageBreak: 'avoid',
        willDrawPage: () => {
            doc.setFillColor(255, 255, 255);
            doc.rect(
                0,
                0,
                doc.internal.pageSize.getWidth(),
                doc.internal.pageSize.getHeight(),
                'F',
            );
            doc.setFontSize(12);
            doc.setTextColor(0);
            doc.text(titleLines, marginX, 16);
        },
    });
    const pageCount = doc.getNumberOfPages();
    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
        doc.setPage(pageNumber);
        doc.setFontSize(7);
        doc.setTextColor(100);
        doc.text(
            `${pageNumber} / ${pageCount}`,
            doc.internal.pageSize.getWidth() - marginX,
            doc.internal.pageSize.getHeight() - 7,
            { align: 'right' },
        );
    }
    return doc.output('blob');
}
